import { faTimes, faMoneyBillWave, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import { formatDate, formatDateTimeUA } from 'helpers/formatDate';
import { getCurrentTime } from 'helpers/timeUtils';
import { scrollDate } from 'helpers/scrollDate';
import { MoneyAmount } from 'components/MoneyAmount';
import { DatePicker } from 'components/DatePicker';
import { TimePicker } from 'components/TimePicker';
import { Checkbox } from 'components/Checkbox';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import styles from './PaymentModal.scss';

export const PaymentModal = ({ isShowModal, mode, payment, entity, entityType, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    amount: '',
    dueDate: formatDate(new Date()),
    dueTime: getCurrentTime(),
    subscriptionID: '',
    entryID: '',
    paid: false,
  });

  const [minutesError, setMinutesError] = useState({});
  const [hoursError, setHoursError] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isShowModal) return;

    setFormData({
      amount: '',
      dueDate: formatDate(new Date()),
      dueTime: getCurrentTime(),
      subscriptionID: '',
      entryID: '',
      paid: false,
    });
    setMinutesError({});
    setHoursError({});
    setErrors({});
    setIsSubmitting(false);
  }, [isShowModal]);

  useEffect(() => {
    if (payment && (mode === 'edit' || mode === 'view')) {
      let dueDate = formatDate(new Date());
      let dueTime = getCurrentTime();

      if (payment.dueTime) {
        const dueDateObj = new Date(payment.dueTime);
        dueDate = formatDate(dueDateObj);
        dueTime = dueDateObj.toLocaleTimeString('uk-UA', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
      }

      setFormData({
        amount: payment.amount || '',
        dueDate,
        dueTime,
        subscriptionID: payment.subscriptionID || '',
        entryID: payment.entryID || '',
        paid: payment.paid || false,
      });
    } else if (mode === 'create') {
      const entityData = {};
      entityData[entityType === 'client' ? 'clientID' : 'coachID'] = entity.id;
      setFormData((prev) => ({ ...prev, ...entityData }));
    }
  }, [isShowModal, payment, mode, entity, entityType]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const combineDateAndTime = (date, time) => {
    if (!date || !time) return null;

    let dateObj = new Date(date);

    const [hours, minutes] = time.split(':').map(Number);

    dateObj.setHours(hours, minutes, 0, 0);

    return dateObj;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Сума повинна бути більше 0';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = "Дата терміну оплати обов'язкова";
    }

    if (!formData.dueTime) {
      newErrors.dueTime = "Час терміну оплати обов'язковий";
    }

    if (formData.dueDate && formData.dueTime) {
      const combinedDateTime = combineDateAndTime(formData.dueDate, formData.dueTime);

      if (!combinedDateTime || isNaN(combinedDateTime.getTime())) {
        newErrors.dueDate = 'Невірний формат дати або часу';
      } else {
        const now = new Date();
        if (combinedDateTime < now) {
          newErrors.dueTime = 'Термін оплати не може бути в минулому';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'view') return;

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const combinedDateTime = combineDateAndTime(formData.dueDate, formData.dueTime);

      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        dueTime: combinedDateTime.toISOString(),
        [entityType === 'client' ? 'clientID' : 'coachID']: entity.id,
        subscriptionID: formData.subscriptionID || null,
        entryID: formData.entryID || null,
      };

      delete submitData.dueDate;

      await onSave(submitData);
      onClose();
    } catch (error) {
      console.error('Error saving payment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDueDateChange = useCallback(
    (direction) => handleInputChange('dueDate', scrollDate(formData.dueDate, direction)),
    [handleInputChange]
  );

  const getTitle = () => {
    switch (mode) {
      case 'create':
        return 'Створити рахунок';
      case 'edit':
        return 'Редагувати рахунок';
      case 'view':
        return 'Перегляд рахунку';
      default:
        return 'Рахунок';
    }
  };

  const isReadonly = mode === 'view';

  return (
    <div className={cn(styles.overlay, { [styles.active]: isShowModal })} onClick={handleBackdropClick}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            <FontAwesomeIcon icon={faMoneyBillWave} className={styles.titleIcon} />
            {getTitle()}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form className={styles.body} onSubmit={handleSubmit}>
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Основна інформація</h3>
            <div className={styles.formGrid}>
              <div>
                <label className={styles.label}>
                  <FontAwesomeIcon icon={faMoneyBillWave} className={styles.icon} />
                  Сума *
                </label>
                <Input
                  className={styles.input}
                  type='number'
                  step='10'
                  min='0'
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  placeholder='0'
                  errorText={errors.amount}
                  invalid={errors.amount}
                  disabled={isReadonly}
                />
                <Checkbox
                  className={styles.paid}
                  label='Оплачено'
                  checked={formData.paid}
                  onChange={(e) => handleInputChange('paid', e.target.checked)}
                  disabled={isReadonly}
                />
              </div>

              <div>
                <label className={styles.label}>
                  <FontAwesomeIcon icon={faCalendarAlt} className={styles.icon} />
                  Термін оплати *
                </label>
                {isReadonly ? (
                  <Input
                    value={formatDateTimeUA(combineDateAndTime(formData.dueDate, formData.dueTime), true)}
                    disabled
                  />
                ) : (
                  <>
                    <DatePicker
                      className={styles.datePicker}
                      date={formData.dueDate}
                      handleScrollDate={handleDueDateChange}
                      enableCalendar
                    />
                    <TimePicker
                      hoursValue={formData.dueTime.split(':')[0]}
                      minutesValue={formData.dueTime.split(':')[1]}
                      setTime={(e) => handleInputChange('dueTime', e.target.value)}
                      name='dueTime'
                      hoursError={hoursError}
                      minutesError={minutesError}
                      setMinutesError={setMinutesError}
                      setHoursError={setHoursError}
                      initialTime={formData.dueTime}
                    />
                  </>
                )}
              </div>
            </div>
            {errors.dueTime && <p className={styles.error}>{errors.dueTime}</p>}
          </div>

          {payment?.transactions.length > 0 && (
            <div className={styles.transactionsSection}>
              <h4 className={styles.sectionTitle}>Транзакції</h4>
              <div className={styles.list}>
                {payment.transactions.length > 0 &&
                  payment.transactions.map((t) => (
                    <div className={styles.card} key={t.id}>
                      <div className={styles.transactionHeader}>
                        <span className={styles.method}>{t.method || 'Не вказано'}</span>
                        <MoneyAmount className={styles.amount} value={t.amount} />
                      </div>
                      <div className={styles.details}>
                        <span className={styles.id}>#{t.id}</span>
                        <span>{formatDateTimeUA(t.createdAt, true)}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {mode === 'view' && payment && (
            <div className={cn(styles.formSection, styles.additionalInfo)}>
              <h3 className={styles.subsectionTitle}>Додаткова інформація</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Призначення:</span>
                  <span className={styles.infoValue}>
                    {payment.subscriptionID ? 'Пакет' : payment.entryID ? 'Заняття' : 'Інше'}
                    {(payment.entryID || payment.subscriptionID) && ` (#${payment.entryID ?? payment.subscriptionID})`}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>ID Рахунку:</span>
                  <span className={styles.infoValue}>#{payment.id}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Створено:</span>
                  <span className={styles.infoValue}>{formatDateTimeUA(payment.createdAt, true)}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Оновлено:</span>
                  <span className={styles.infoValue}>{formatDateTimeUA(payment.updatedAt, true)}</span>
                </div>
              </div>
            </div>
          )}
        </form>

        <div className={styles.footer}>
          <Button onClick={onClose} smallStyle ghostStyle>
            {mode === 'view' ? 'Закрити' : 'Скасувати'}
          </Button>
          {!isReadonly && (
            <Button type='submit' onClick={handleSubmit} disabled={isSubmitting} smallStyle>
              {isSubmitting ? 'Збереження...' : mode === 'create' ? 'Створити' : 'Зберегти'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
